const React = window.React || null;
const { useEffect, useState, useMemo, useRef } = React;
import ProfileIcon from './ProfileIcon.tsx';
import UserNavDropdown from './UserNavDropdown.tsx';

type UserNavItem = {
    ID?: number;
    title?: string;
    url?: string;
    target?: string;
    classes?: string[];
};


const user_type_and_css_class_map: Record<string, string> = {
    'anonymous': 'anonymous-link',
    'registered': 'registered-link',
    'subscriber': 'subscriber-link',
    'corp-subscriber': 'corp-subscriber-link',
    'corp-admin': 'corp-admin-link',
};

function UserNav(){
    const DESKTOP_HOVER_MIN_WIDTH = 992;

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [hoverEnabled, setHoverEnabled] = useState(false);
    const [isClickPinnedOpen, setIsClickPinnedOpen] = useState(false);
    const refDropdownWrapper = useRef<HTMLDivElement>(null);
    const isAuthenticated = window?.Zephr?.accessDetails?.isAuthenticated || false;
    const eventRef = useRef<AbortController>(new AbortController());
    
    const [isCorpAdmin, setIsCorpAdmin] = useState(() => window.Cookies?.get('aw_user_is_corp_admin') === 'true');


    const currentUserType = useMemo(() => {
        if (!isAuthenticated) return 'anonymous';
        if (isCorpAdmin) return 'corp-admin';
        const activeProducts = Zephr?.accessDetails?.activeProducts || [];
        if (activeProducts.some((p: string) => p.toLowerCase().includes('corp'))) return 'corp-subscriber';
        if (activeProducts.length > 0) return 'subscriber';
        return 'registered';
    }, [isCorpAdmin]);

    const userNavItems = useMemo(() => {
        const typeClasses = new Set(Object.values(user_type_and_css_class_map));
        const currentTypeClass = user_type_and_css_class_map[currentUserType];

        return window?.adw?.nav_user_context?.items?.filter((item: UserNavItem) => {
            if (!Boolean(item?.title && item?.url)) return false;
            const itemTypeClasses = item?.classes?.filter((cls) => typeClasses.has(cls)) ?? [];
            if (itemTypeClasses.length === 0) return true;
            return itemTypeClasses.includes(currentTypeClass);
        }).map((item: UserNavItem) => ({
            ...item,
            classes: item?.classes?.filter((cls) => cls !== 'd-none'),
        })) || [];
    }, [currentUserType]);

    useEffect(() => {
        const typeClasses = Object.values(user_type_and_css_class_map);
        const currentTypeClass = user_type_and_css_class_map[currentUserType];
        const selector = typeClasses.map((c) => `.${c}`).join(',');

        document.querySelectorAll(selector).forEach((el) => {
            const elTypeClasses = typeClasses.filter((c) => el.classList.contains(c));
            const shouldShow = elTypeClasses.some((c) => c === currentTypeClass);
            el.classList.toggle('d-none', !shouldShow);
        });
    }, [currentUserType]);

    useEffect(() => {
        const signal = eventRef.current.signal;
        const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');

        const updateHoverEnabled = () => {
            const nextHoverEnabled = window.innerWidth >= DESKTOP_HOVER_MIN_WIDTH && hoverMedia.matches;
            setHoverEnabled(nextHoverEnabled);
            if (!nextHoverEnabled) {
                setIsClickPinnedOpen(false);
            }
        };

        updateHoverEnabled();
        window.addEventListener('resize', updateHoverEnabled, { signal });
        hoverMedia.addEventListener('change', updateHoverEnabled, { signal });

        document.addEventListener('click', (event: MouseEvent) => {
            if (refDropdownWrapper.current && !refDropdownWrapper.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
                setIsClickPinnedOpen(false);
            }
        }, { signal });

        document.addEventListener('adw:corpAdminResolved', (event: Event) => {
            const { isCorpAdmin: resolved } = (event as CustomEvent<{ isCorpAdmin: boolean }>).detail;
            setIsCorpAdmin(resolved);
        }, { signal });

        const elUserNav = document.querySelector('.user-nav');
        if (elUserNav) {
            elUserNav.classList.remove('opacity-0');
            if ('function' === typeof anime) {
                anime({
                    targets: elUserNav,
                    opacity: [0, 1],
                    duration: 300,
                    easing: 'easeOutQuad',
                    delay: 100,
                });
            }
        }

        const controller = eventRef.current;
        return () => controller.abort();
    }, []);

    const loginUrl = useMemo(() => {
        let url = window?.adw?.account_login_url || null;
        if (url) {
            // If url already has query params, append with &, otherwise with ?
            const separator = url.includes('?') ? '&' : '?';
            url = `${url}${separator}rdt=${window.location.href}`;
        }
        return url;
    }, []);

    return (
        <>
            { isAuthenticated ?
            <div
                className="position-relative"
                ref={refDropdownWrapper}
                onMouseEnter={() => {
                    if( hoverEnabled && userNavItems.length > 0 ){
                        setIsDropdownOpen(true);
                    }
                }}
                onMouseLeave={() => {
                    if( hoverEnabled && !isClickPinnedOpen ){
                        setIsDropdownOpen(false);
                    }
                }}
            >
                <button
                    type="button"
                    className={`btn btn-link rounded-pill ${ isClickPinnedOpen ? '' : 'border' } ps-3 pe-2 lh-1 text-decoration-none align-self-end d-inline-flex align-items-center px-0 fw-bold gap-1 ${isDropdownOpen ? 'bg-light-subtle text-primary' : ''}`}
                    onClick={() => {
                        if( hoverEnabled ){
                            if( isDropdownOpen && isClickPinnedOpen ){
                                setIsDropdownOpen(false);
                                setIsClickPinnedOpen(false);
                            }else {
                                setIsDropdownOpen(true);
                                setIsClickPinnedOpen(true);
                            }
                            return;
                        }

                        setIsDropdownOpen((prevState) => !prevState);
                    }}
                    aria-expanded={isDropdownOpen}
                    aria-haspopup="menu"
                    style={{
                        background: 'var(--bs-body-bg)',
                        boxShadow: isClickPinnedOpen ? 'inset 0 0 3px var(--bs-primary)' : 'none',
                        
                     }}
                >
                    { window.awZephrUserData?.firstName ? <span>{`Hey, ${window.awZephrUserData.firstName}`}</span> : <span>Hello</span> }
                    <span className="position-relative">
                        {/* {
                            isAuthenticated &&
                            <span
                                className="position-absolute top-0 end-0 translate-middle p-1 bg-warning rounded-circle"
                            >
                            </span>
                        } */}
                        <ProfileIcon className="fs-4" />
                    </span>
                </button>

                <UserNavDropdown items={userNavItems} isOpen={isDropdownOpen} />
            </div>
            : 
                (
                    <div className="user-nav d-flex align-items-center gap-2 flex-shrink-0 ">

                        <div className="d-flex gap-3" >

                            <a href={window.adw?.account_subscribe_url ?? '#'} className="btn btn-link text-decoration-none align-self-end d-inline-block px-0">Subscribe</a>

                            <a href={loginUrl ? loginUrl : '#'} className="btn-signin btn btn-link text-decoration-none align-self-end d-inline-block px-0">Sign In</a>

                        </div>

                    </div>
                )
            
            }
            
        </>
    );
}

export default UserNav;